import { Link, useLocation } from 'react-router-dom';

import { AppShell, Burger, Button, Drawer, Group, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconInbox, IconMailQuestion, IconPlus, IconReportAnalytics } from '@tabler/icons-react';

import { LogoutButton } from '@/components';
import { HeaderLogo } from '@/components/Logo';
import { useAuth } from '@/contexts/AuthContext';
import { useInboxCount, useRegistryAttention } from '@/hooks';
import { AdminMenu } from './AdminMenu';
import { navCountBadge } from './navCountBadge';
import { ProfilePill } from './ProfilePill';
import classes from './TopNav.module.css';

interface PrimaryNavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  matches: (pathname: string) => boolean;
}

const PRIMARY_NAV: PrimaryNavItem[] = [
  {
    to: '/reports',
    label: 'Reports',
    icon: <IconReportAnalytics size={18} />,
    matches: (p) => p.startsWith('/reports'),
  },
];

const ADMIN_INBOX: PrimaryNavItem = {
  to: '/inbox',
  label: 'Inbox',
  icon: <IconInbox size={18} />,
  matches: (p) => p.startsWith('/inbox'),
};

// Client-only: their team's report requests (the pre-report pipeline).
// Admins work requests through Inbox / Cases instead.
const CLIENT_REQUESTS: PrimaryNavItem = {
  to: '/requests',
  label: 'Requests',
  icon: <IconMailQuestion size={18} />,
  matches: (p) => p.startsWith('/requests'),
};

export function TopNav() {
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);

  const isAdmin = !!user?.isAdministrator;
  const navItems = isAdmin ? [...PRIMARY_NAV, ADMIN_INBOX] : [...PRIMARY_NAV, CLIENT_REQUESTS];
  // Needs-you count on the Inbox item (admin-only endpoint).
  const inboxCount = useInboxCount(isAdmin);
  // Registry documents that still need someone, on the Admin menu
  // (admin-only endpoint).
  const registryAttention = useRegistryAttention(isAdmin);

  const badgeFor = (item: PrimaryNavItem) =>
    item.to === '/inbox' ? navCountBadge(inboxCount, 'inbox-nav-badge') : undefined;

  return (
    <AppShell.Header>
      <div className={classes.bar}>
        <div className={classes.leftGroup}>
          <Burger
            opened={mobileOpen}
            onClick={toggleMobile}
            hiddenFrom="sm"
            aria-label="Toggle navigation"
            className={classes.burger}
            color="white"
            size="sm"
          />
          <HeaderLogo />
        </div>
        <Group gap="sm" visibleFrom="sm" className={classes.centerGroup}>
          <Button
            component={Link}
            to="/request-report"
            variant="filled"
            color="yellow"
            leftSection={<IconPlus size={16} />}
            className={classes.ctaButton}
          >
            Request Report
          </Button>
          <div className={classes.navLinks}>
            {navItems.map((item) => {
              const active = item.matches(location.pathname);
              return (
                <Button
                  key={item.to}
                  component={Link}
                  to={item.to}
                  variant="subtle"
                  leftSection={item.icon}
                  rightSection={badgeFor(item)}
                  className={`${classes.navButton} ${active ? classes.navButtonActive : ''}`}
                  styles={{
                    root: { color: 'var(--mantine-color-white)' },
                    label: { color: 'var(--mantine-color-white)' },
                    section: { color: 'var(--mantine-color-white)' },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
            {isAdmin && <AdminMenu registryAttention={registryAttention} />}
          </div>
        </Group>
        <div className={classes.rightGroup}>
          <ProfilePill />
          <LogoutButton iconOnly />
        </div>
      </div>

      <Drawer
        opened={mobileOpen}
        onClose={closeMobile}
        position="left"
        size="xs"
        title="Navigation"
        hiddenFrom="sm"
      >
        <Stack className={classes.mobileDrawerNavList}>
          <Button
            component={Link}
            to="/request-report"
            variant="filled"
            color="yellow"
            leftSection={<IconPlus size={16} />}
            onClick={closeMobile}
          >
            Request Report
          </Button>
          {navItems.map((item) => (
            <Button
              key={item.to}
              component={Link}
              to={item.to}
              variant="subtle"
              color="navy"
              leftSection={item.icon}
              rightSection={badgeFor(item)}
              onClick={closeMobile}
              justify="flex-start"
            >
              {item.label}
            </Button>
          ))}
          {isAdmin && (
            <>
              <Button
                component={Link}
                to="/admin/report-configs"
                variant="subtle"
                color="navy"
                onClick={closeMobile}
                justify="flex-start"
              >
                Admin · Report Configs
              </Button>
              <Button
                component={Link}
                to="/admin/scope-components"
                variant="subtle"
                color="navy"
                onClick={closeMobile}
                justify="flex-start"
              >
                Admin · Scope Components
              </Button>
              <Button
                component={Link}
                to="/admin/network-partners"
                variant="subtle"
                color="navy"
                onClick={closeMobile}
                justify="flex-start"
              >
                Admin · Network Partners
              </Button>
              <Button
                component={Link}
                to="/admin/registry-documents"
                variant="subtle"
                color="navy"
                rightSection={navCountBadge(
                  registryAttention?.total,
                  'admin-drawer-registry-badge'
                )}
                onClick={closeMobile}
                justify="flex-start"
              >
                Admin · Registry Documents
              </Button>
              <Button
                component={Link}
                to="/inbox?tab=browse"
                variant="subtle"
                color="navy"
                onClick={closeMobile}
                justify="flex-start"
              >
                Admin · Browse Cases
              </Button>
            </>
          )}
        </Stack>
      </Drawer>
    </AppShell.Header>
  );
}
